<template>
  <b-modal
    id="configModal"
    v-model="isVisible"
    title="Cấu hình"
    size="lg"
    hide-footer
    @hidden="onHidden"
  >
    <div class="config-container">
      <!-- Loading state -->
      <div v-if="isLoading" class="text-center py-4">
        <b-spinner class="align-middle" />
        <div class="mt-2">Đang tải cấu hình...</div>
      </div>

      <!-- Main content -->
      <div v-else>
        <div class="mappings-wrapper">
          <div class="field-mapping" v-for="(mapping, index) in fieldMappings" :key="index">
            <div class="mapping-row">
              <div class="excel-field">
                <label>Trường MNP</label>
                <input
                  type="text"
                  v-model="mapping.fieldMnp"
                  class="form-control"
                  placeholder="Nhập tên trường MNP"
                />
              </div>

              <div class="mautic-field">
                <label>Trường trong Mautic</label>
                <input
                  type="text"
                  v-model="mapping.fieldMautic"
                  class="form-control"
                  placeholder="Nhập tên trường Mautic"
                />
              </div>

              <div class="status-field">
                <label>Trạng thái</label>
                <div class="status-toggle mt-2">
                  <label class="switch">
                    <input type="checkbox" v-model="mapping.fieldStatus" :disabled="!mapping.id" />
                    <span class="slider round" :class="{ disabled: !mapping.id }"></span>
                  </label>
                  <span
                    class="status-text"
                    :class="{
                      'text-success': mapping.fieldStatus,
                      'text-muted': !mapping.fieldStatus,
                    }"
                  >
                    {{ mapping.fieldStatus ? 'Kích hoạt' : 'Tắt' }}
                  </span>
                </div>
              </div>

              <div class="action-field">
                <button
                  type="button"
                  class="btn btn-danger btn-sm mt-4"
                  @click="removeMapping(index)"
                  :disabled="fieldMappings.length <= 1"
                >
                  X
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="add-mapping">
          <button type="button" class="btn btn-primary" @click="addMapping">Thêm</button>
        </div>

        <div class="modal-actions mt-4">
          <button type="button" class="btn btn-secondary mr-2" @click="cancel" :disabled="isSaving">
            Hủy
          </button>
          <button type="button" class="btn btn-primary" @click="save" :disabled="isSaving">
            <b-spinner small v-if="isSaving" class="mr-1" />
            {{ isSaving ? 'Đang lưu...' : 'Lưu' }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
  import { showMess } from '@/config/utils'
  import { MauticService } from '@/services/mautic.service'

  export default {
    name: 'ConfigModal',
    props: {
      visible: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isVisible: false,
        fieldMappings: [{ id: null, fieldMnp: '', fieldMautic: '', fieldStatus: true }],
        originalMappings: [], // Store original data for comparison
        isLoading: false,
        isSaving: false,
      }
    },
    watch: {
      visible: {
        immediate: true,
        handler(newVal) {
          this.isVisible = newVal
          if (newVal) {
            this.loadConfigMappings()
          }
        },
      },

      // Debug method
      debugInput(field, event, index) {
        console.log(`Input ${field} at index ${index}:`, event.target.value)
        console.log('Current fieldMappings:', this.fieldMappings)
      },
      isVisible(newVal) {
        this.$emit('update:visible', newVal)
      },
    },
    methods: {
      addMapping() {
        this.fieldMappings.push({
          id: null,
          fieldMnp: '',
          fieldMautic: '',
          fieldStatus: true,
        })

        // Scroll to bottom after adding new mapping
        this.$nextTick(() => {
          const modal = document.querySelector('#configModal')
          const wrapper = modal?.querySelector('.mappings-wrapper')
          if (wrapper) {
            wrapper.scrollTop = wrapper.scrollHeight
          }
        })
      },

      removeMapping(index) {
        if (this.fieldMappings.length > 1) {
          this.fieldMappings.splice(index, 1)
        }
      },

      save() {
        // Validate mappings
        const validMappings = this.fieldMappings.filter(
          m => m.fieldMnp && m.fieldMnp.trim() && m.fieldMautic && m.fieldMautic.trim()
        )

        if (validMappings.length === 0) {
          showMess(
            this,
            'Warning',
            'Vui lòng cấu hình ít nhất một mapping',
            'warning',
            'b-toaster-top-right',
            5000
          )
          return
        }

        // Trim whitespace
        validMappings.forEach(mapping => {
          mapping.fieldMnp = mapping.fieldMnp.trim()
          mapping.fieldMautic = mapping.fieldMautic.trim()
        })

        // Check duplicate mappings
        const duplicateMnp = validMappings.some(
          (mapping, index) =>
            validMappings.findIndex(
              m => m.fieldMnp.toLowerCase() === mapping.fieldMnp.toLowerCase()
            ) !== index
        )

        const duplicateMautic = validMappings.some(
          (mapping, index) =>
            validMappings.findIndex(
              m => m.fieldMautic.toLowerCase() === mapping.fieldMautic.toLowerCase()
            ) !== index
        )

        if (duplicateMnp || duplicateMautic) {
          showMess(
            this,
            'Warning',
            'Không được trùng lặp tên trường',
            'warning',
            'b-toaster-top-right',
            5000
          )
          return
        }

        // Filter only changed mappings
        const changedMappings = this.getChangedMappings(validMappings)

        if (changedMappings.length === 0) {
          showMess(
            this,
            'Info',
            'Không có thay đổi nào để lưu',
            'info',
            'b-toaster-top-right',
            3000
          )
          return
        }

        this.saveConfigMappings(changedMappings)
      },

      getChangedMappings(validMappings) {
        const changedMappings = []

        validMappings.forEach(mapping => {
          // New mapping (no id)
          if (!mapping.id) {
            changedMappings.push({
              type: 'new',
              data: {
                fieldMnp: mapping.fieldMnp,
                fieldMautic: mapping.fieldMautic,
              },
            })
          } else {
            // Existing mapping - check if changed
            const original = this.originalMappings.find(orig => orig.id === mapping.id)
            if (original) {
              const hasChanged =
                original.fieldMnp !== mapping.fieldMnp ||
                original.fieldMautic !== mapping.fieldMautic ||
                original.fieldStatus !== mapping.fieldStatus

              if (hasChanged) {
                changedMappings.push({
                  type: 'update',
                  data: {
                    id: mapping.id,
                    fieldMnp: mapping.fieldMnp,
                    fieldMautic: mapping.fieldMautic,
                    fieldStatus: mapping.fieldStatus,
                  },
                })
              }
            }
          }
        })

        return changedMappings
      },

      async saveConfigMappings(changedMappings) {
        try {
          this.isSaving = true

          // Gọi API cho từng mapping đã thay đổi
          const savePromises = changedMappings.map(item => {
            return MauticService.saveFieldMappings(item.data)
          })

          const results = await Promise.all(savePromises)

          // Kiểm tra tất cả results
          const allSuccess = results.every(res => res.success)

          if (allSuccess) {
            showMess(
              this,
              'Success',
              `Lưu thành công ${changedMappings.length} cấu hình`,
              'success',
              'b-toaster-top-right',
              5000
            )

            // Reload data after successful save
            await this.loadConfigMappings()
            this.$emit('config-saved', this.fieldMappings)
            this.close()
          } else {
            const failedResults = results.filter(res => !res.success)
            showMess(
              this,
              'Error',
              `Có ${failedResults.length} cấu hình lưu thất bại`,
              'danger',
              'b-toaster-top-right',
              5000
            )
          }
        } catch (error) {
          console.error('Save error:', error)

          // Handle ErrorWrapper format
          const errorMessage =
            error.message ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            'Có lỗi xảy ra khi lưu cấu hình'

          showMess(this, 'Error', errorMessage, 'danger', 'b-toaster-top-right', 5000)
        } finally {
          this.isSaving = false
        }
      },

      cancel() {
        this.loadConfigMappings()
        this.close()
      },

      close() {
        this.isVisible = false
      },

      onHidden() {
        this.$emit('hidden')
      },

      async loadConfigMappings() {
        try {
          this.isLoading = true

          const res = await MauticService.getFieldMappings()

          if (res.code === 1000) {
            if (res.data && res.data.length > 0) {
              // Debug log to check API response
              console.log('API Response:', res.data)

              // Map API response to component data structure
              this.fieldMappings = res.data.map(item => ({
                id: item.id,
                fieldMnp: item.fieldMnp || '',
                fieldMautic: item.fieldMautic || '',
                fieldStatus: item.fieldStatus !== false, // default true if undefined
              }))

              // Debug log to check mapped data
              console.log('Mapped fieldMappings:', this.fieldMappings)
            } else {
              this.fieldMappings = [{ id: null, fieldMnp: '', fieldMautic: '', fieldStatus: true }]
            }
          } else {
            // Fallback to default if API fails
            this.fieldMappings = [{ id: null, fieldMnp: '', fieldMautic: '', fieldStatus: true }]
            console.log('API failed with code:', res.code)
          }
        } catch (error) {
          console.error('Error loading field mappings:', error)
          // Fallback to default if error
          this.fieldMappings = [{ id: null, fieldMnp: '', fieldMautic: '', fieldStatus: true }]

          showMess(
            this,
            'Warning',
            'Không thể tải cấu hình, sử dụng cấu hình mặc định',
            'warning',
            'b-toaster-top-right',
            5000
          )
        } finally {
          this.isLoading = false
        }
      },
    },
  }
</script>

<style scoped>
  .config-container {
    padding: 20px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .mappings-wrapper {
    max-height: 400px;
    overflow-y: auto;
    padding-right: 10px;
    margin-right: -10px;
  }

  .mappings-wrapper::-webkit-scrollbar {
    width: 6px;
  }

  .mappings-wrapper::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .mappings-wrapper::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .mappings-wrapper::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .field-mapping {
    margin-bottom: 15px;
  }

  .mapping-row {
    display: flex;
    align-items: flex-end;
    gap: 15px;
  }

  .excel-field,
  .mautic-field {
    flex: 2;
  }

  .status-field {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .status-field label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    color: #495057;
    font-size: 14px;
  }

  .form-check {
    display: flex;
    align-items: center;
    margin-top: 8px;
  }

  .form-check-input {
    margin-right: 8px;
  }

  .form-check-label {
    font-size: 13px;
    color: #495057;
    margin-bottom: 0;
  }

  /* Toggle Switch Styles */
  .status-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 24px;
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
  }

  .slider:before {
    position: absolute;
    content: '';
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
  }

  input:checked + .slider {
    background-color: #28a745;
  }

  input:focus + .slider {
    box-shadow: 0 0 1px #28a745;
  }

  input:checked + .slider:before {
    transform: translateX(26px);
  }

  .slider.round {
    border-radius: 24px;
  }

  .slider.round:before {
    border-radius: 50%;
  }

  .slider.disabled {
    background-color: #e9ecef !important;
    cursor: not-allowed;
  }

  .slider.disabled:before {
    background-color: #f8f9fa !important;
  }

  .switch input:disabled + .slider {
    cursor: not-allowed;
  }

  .status-text {
    font-size: 13px;
    font-weight: 500;
    min-width: 60px;
  }

  .text-success {
    color: #28a745 !important;
  }

  .text-muted {
    color: #6c757d !important;
  }

  .excel-field label,
  .mautic-field label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    color: #495057;
    font-size: 14px;
  }

  .action-field {
    width: 40px;
  }

  .form-control {
    font-size: 13px;
  }

  .btn-sm {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .add-mapping {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #dee2e6;
    flex-shrink: 0;
  }

  .modal-actions {
    padding-top: 15px;
    border-top: 1px solid #dee2e6;
    text-align: right;
    flex-shrink: 0;
  }

  .btn-secondary,
  .btn-primary {
    min-width: 80px;
  }

  /* Đảm bảo scroll hoạt động tốt trên các trình duyệt khác */
  .mappings-wrapper {
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
  }
</style>
